import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { PageHeader, Modal, Toast, PasswordInput } from "@/components/Layout";
import api from "@/lib/api";
import useFilters from "@/lib/useFilters";
import { downloadFile, downloadPost, queryString } from "@/lib/download";
import FilterActions from "@/components/FilterActions";
import FormTabs, { tabStyle } from "@/components/FormTabs";
import MultiSelect from "@/components/MultiSelect";
import RegesFields from "@/components/RegesFields";
import CustomFields, { useCustomDefs } from "@/components/CustomFields";
import Select from "@/components/Select";
import ViewOnly from "@/components/ViewOnly";
import { useAuth } from "@/context/AuthContext";
import { lock } from "@/lib/perm";
import RowActions from "@/components/RowActions";

const EMAIL_RE = /^[^@\s]+@[^@\s]+\.[^@\s]+$/;
const STATUS = { active: ["Activ", "badge-active"], suspended: ["Suspendat", ""], terminated: ["Încetat", "badge-inactive"] };
const fold = (x) => String(x ?? "").normalize("NFD").replace(/[̀-ͯ]/g, "").toLowerCase().trim();
const EMPTY = { first_name: "", last_name: "", cnp: "", birth_date: "", phone: "" };
const COLUMNS_KEY = "persons_columns_v1";
const BASE_COLUMNS = ["last_name", "first_name", "cnp", "birth_date", "active_contracts"];

// Angajații (persoanele): datele personale, separate de contul de autentificare. Un angajat poate avea un cont
// (acces în platformă) și unul sau mai multe rapoarte de muncă; rapoartele se modifică din „Rapoarte de Muncă”,
// contul (roluri, parolă) din „Utilizatori și Roluri”. Coloanele listei se aleg din butonul „Coloane” (ca la Stat de funcții);
// exporturile Excel și PDF urmează aceleași coloane și aceleași filtre.
export default function Persons() {
  const { has } = useAuth();
  const navigate = useNavigate();
  const [rows, setRows] = useState([]);
  const [colMeta, setColMeta] = useState([]);
  const [units, setUnits] = useState([]);
  const [editing, setEditing] = useState(null);   // { id?, ...câmpuri, reges, custom, view, employments }
  const [tab, setTab] = useState("general");
  const [grant, setGrant] = useState(null);       // { person, email, password }
  const [grantDone, setGrantDone] = useState(null);
  const [revoke, setRevoke] = useState(null);
  const [gdprExp, setGdprExp] = useState(null);   // { person, pw } — exportul tuturor datelor unei persoane (GDPR)
  const [msg, setMsg] = useState({ text: "", type: "info" });
  const f = useFilters({ last: "", first: "", cnp: "", account: "", empl: "" });
  const a = f.applied;
  const customDefs = useCustomDefs("person");

  const [cols, setCols] = useState(() => {
    try { const v = JSON.parse(localStorage.getItem(COLUMNS_KEY)); return Array.isArray(v) && v.length ? v : BASE_COLUMNS; } catch { return BASE_COLUMNS; }
  });
  const pickCols = (v) => { setCols(v); try { localStorage.setItem(COLUMNS_KEY, JSON.stringify(v)); } catch { /* fără stocare */ } };
  // coloanele rămase fără sens (câmp dezactivat, drept retras) ies din alegere; ordinea e cea din definiție
  const activeCols = useMemo(() => {
    if (!colMeta.length) return BASE_COLUMNS.map(k => ({ key: k, label: k }));
    const ok = colMeta.filter(c => cols.includes(c.key));
    return ok.length ? ok : colMeta.filter(c => c.default);
  }, [colMeta, cols]);
  const colParam = activeCols.map(c => c.key).join(",");

  const load = () => api.get("/persons", { params: { columns: colParam } }).then(r => setRows(r.data)).catch(() => {});
  useEffect(() => { api.get("/persons/columns").then(r => setColMeta(r.data.columns)).catch(() => {}); }, []);
  useEffect(() => { api.get("/mdm/org-units").then(r => setUnits(r.data)).catch(() => {}); }, []);
  useEffect(() => { if (colMeta.length) load(); }, [colParam, colMeta.length]); // eslint-disable-line react-hooks/exhaustive-deps

  const flash = (text, type = "success", ms = 2500) => { setMsg({ text, type }); setTimeout(() => setMsg({ text: "" }), ms); };
  const fail = (e) => setMsg({ text: e?.response?.data?.detail || "Eroare", type: "error" });

  const filtered = rows.filter(p =>
    (!a.last || fold(p.last_name).includes(fold(a.last))) &&
    (!a.first || fold(p.first_name).includes(fold(a.first))) &&
    (!a.cnp || String(p.cnp || "").includes(a.cnp.trim())) &&
    (!a.account || (a.account === "da") === p.has_account) &&
    (!a.empl || (a.empl === "da") === (p.employments_count > 0)));
  const anyFilter = !!(a.last || a.first || a.cnp || a.account || a.empl);

  const exportFile = async (fmt) => {
    try {
      await downloadFile(`/persons/export.${fmt}` + queryString({ columns: colParam, last_name: a.last, first_name: a.first, cnp: a.cnp, access: a.account, contracts: a.empl }),
        `lista_angajati.${fmt}`);
    } catch (e) { setMsg({ text: e.message, type: "error" }); }
  };

  const open = async (p, view) => {
    try {
      const r = await api.get(`/persons/${p.id}`);
      setTab("general");
      setEditing({ ...r.data, birth_date: r.data.birth_date || "", reges: r.data.reges || {}, custom: r.data.custom || {}, view });
    } catch (e) { fail(e); }
  };
  const openNew = () => { setTab("general"); setEditing({ ...EMPTY, view: false, reges: {}, custom: {}, employments: [], sensitive_visible: has("data.view_sensitive") }); };

  const save = async () => {
    if (!editing.first_name?.trim() || !editing.last_name?.trim()) { setTab("general"); return setMsg({ text: "Nume și prenume sunt obligatorii", type: "error" }); }
    const body = { first_name: editing.first_name, last_name: editing.last_name, cnp: editing.cnp || "",
                   birth_date: editing.birth_date || null, phone: editing.phone || "" };
    if (editing.sensitive_visible) {
      body.reges = Object.fromEntries(Object.entries(editing.reges || {}).map(([k, v]) => [k, v == null ? "" : String(v)]));
    }
    const custom = {};
    Object.entries(editing.custom || {}).forEach(([k, v]) => { if (!v.masked) custom[k] = { value: v.value ?? null, employment_ids: v.employment_ids || [] }; });
    body.custom = custom;
    try {
      if (editing.id) await api.put(`/persons/${editing.id}`, body); else await api.post("/persons", body);
      setEditing(null); load(); flash("Salvat");
    } catch (e) { fail(e); }
  };

  const doGrant = async () => {
    if (grant.email && !EMAIL_RE.test(grant.email)) return setMsg({ text: "Adresă de e-mail invalidă (ex: nume@domeniu.ro)", type: "error" });
    if (grant.password && grant.password.length < 12) return setMsg({ text: "Parola trebuie să aibă minim 12 caractere", type: "error" });
    try {
      const r = await api.post(`/users/${grant.person.user_id}/grant-access`, { email: grant.email, password: grant.password });
      setGrantDone({ username: r.data.username, password: r.data.temporary_password, emailed: !!grant.email });
      setGrant(null); load();
    } catch (e) { setGrant(null); fail(e); }
  };
  const doRevoke = async () => {
    try {
      await api.post(`/users/${revoke.user_id}/revoke-access`);
      setRevoke(null); load(); flash("Acces retras. Angajatul rămâne înregistrat, cu rapoartele de muncă.", "success", 3000);
    } catch (e) { setRevoke(null); fail(e); }
  };

  const btn = { padding: "3px 10px", fontSize: 12 };
  const unitName = (id) => units.find(u => u.id === id)?.name || "—";
  const colOption = (c) => ({ value: c.key, label: c.group.startsWith("ReGES") || c.group.startsWith("Câmpuri") ? `${c.label} — ${c.group.replace("ReGES – ", "ReGES: ")}` : c.label });
  const reg = editing?.reges || {};
  const completeness = editing?.reges_completeness;

  return (
    <>
      <PageHeader title="Angajați" subtitle={anyFilter ? `${filtered.length} din ${rows.length} angajați` : `${rows.length} angajați`}
        actions={<button className="btn btn-primary" data-testid="add-person" onClick={openNew} {...lock(has("persons.create"))}>+ Angajat nou</button>} />
      <Toast msg={msg.text} type={msg.type} />
      <form className="card" style={{ padding: 12, marginBottom: 12, display: "flex", flexWrap: "wrap", gap: 10, alignItems: "flex-end" }} data-testid="persons-filters"
        onSubmit={e => { e.preventDefault(); f.apply(); }}>
        <div><label className="label">Nume</label>
          <input className="input" value={f.draft.last} onChange={e => f.set({ last: e.target.value })} placeholder="Caută după nume" data-testid="pf-last" style={{ minWidth: 160 }} /></div>
        <div><label className="label">Prenume</label>
          <input className="input" value={f.draft.first} onChange={e => f.set({ first: e.target.value })} placeholder="Caută după prenume" data-testid="pf-first" style={{ minWidth: 160 }} /></div>
        <div><label className="label">CNP</label>
          <input className="input" value={f.draft.cnp} onChange={e => f.set({ cnp: e.target.value })} placeholder="CNP" data-testid="pf-cnp" style={{ minWidth: 140 }} /></div>
        <div><label className="label">Acces în platformă</label>
          <Select className="select" value={f.draft.account} onChange={e => f.set({ account: e.target.value })} data-testid="pf-account">
            <option value="">Toți</option><option value="da">Cu acces</option><option value="nu">Fără acces</option>
          </Select></div>
        <div><label className="label">Rapoarte de muncă</label>
          <Select className="select" value={f.draft.empl} onChange={e => f.set({ empl: e.target.value })} data-testid="pf-empl">
            <option value="">Toți</option><option value="da">Cu raport de muncă</option><option value="nu">Fără raport de muncă</option>
          </Select></div>
        <FilterActions f={f} testid="persons-filters" />
        <div style={{ display: "flex", gap: 8, flexWrap: "wrap", alignItems: "flex-end" }}>
          <div><label className="label">Coloane afișate</label>
            <MultiSelect testId="persons-columns" minWidth={230} allLabel="Coloane" options={colMeta.map(colOption)}
              value={activeCols.map(c => c.key)} onChange={pickCols} summaryOf={v => `Coloane (${v.length})`} /></div>
          {cols.join() !== BASE_COLUMNS.join() && <button type="button" className="btn btn-secondary" onClick={() => pickCols(BASE_COLUMNS)} data-testid="persons-columns-reset">Coloane implicite</button>}
          <button type="button" className="btn btn-secondary" onClick={() => exportFile("xlsx")} data-testid="persons-export-xlsx" {...lock(has("persons.export"))}>Excel</button>
          <button type="button" className="btn btn-secondary" onClick={() => exportFile("pdf")} data-testid="persons-export-pdf" {...lock(has("persons.export"))}>PDF</button>
        </div>
      </form>
      <div className="card" style={{ overflowX: "auto" }}>
        <table className="tbl tbl-dense" data-testid="persons-table">
          <thead><tr>{activeCols.map(c => <th key={c.key} data-col={c.key}>{c.label}</th>)}<th></th></tr></thead>
          <tbody>{filtered.map(p => (
            <tr key={p.id} data-testid={`person-row-${p.username}`}>
              {activeCols.map(c => (
                <td key={c.key} data-col={c.key} className={["cnp", "birth_date", "active_contracts", "total_contracts"].includes(c.key) ? "tabular-nums" : undefined}
                  style={c.key === "last_name" ? { fontWeight: 600 } : undefined}>
                  {p.cells?.[c.key] || "—"}
                  {c.key === "last_name" && !p.has_account && <span title="Persoană înregistrată fără cont de autentificare" style={{ fontSize: 10, padding: "1px 6px", background: "#E2E8F0", color: "#334155", borderRadius: 3, marginLeft: 6 }}>FĂRĂ ACCES</span>}
                  {c.key === "last_name" && !p.active && <span className="badge badge-inactive" style={{ marginLeft: 6 }}>Inactiv</span>}
                </td>))}
              <td><RowActions column testid={`row-menu-${p.username}`} items={[
                { label: "Vizualizează", icon: "eye", onClick: () => open(p, true), testid: `pview-${p.username}` },
                { label: "Editează", icon: "edit", onClick: () => open(p, false), testid: `pedit-${p.username}`, ...lock(has("persons.update")) },
                p.has_account
                  ? { label: "Revocă acces", icon: "close", danger: true, testid: `prevoke-${p.username}`, ...lock(has("users.update")), onClick: () => setRevoke(p) }
                  : { label: "Acordă acces", icon: "key", primary: true, testid: `pgrant-${p.username}`, ...lock(has("users.update")), onClick: () => setGrant({ person: p, email: p.email || "", password: "" }) },
                { label: "Export GDPR", icon: "download", testid: `pgdpr-${p.username}`, ...lock(has("gdpr.export")), onClick: () => setGdprExp({ person: p, pw: "" }) },
              ]} /></td>
            </tr>))}
            {filtered.length === 0 && <tr><td colSpan={activeCols.length + 1} style={{ textAlign: "center", color: "#94A3B8", padding: 24 }}>Niciun angajat.</td></tr>}
          </tbody>
        </table>
      </div>

      {gdprExp && (
        <Modal title={`Export GDPR — ${gdprExp.person.last_name || ""} ${gdprExp.person.first_name || ""}`} onClose={() => setGdprExp(null)} width={480}>
          <div style={{ fontSize: 13, color: "#334155", marginBottom: 12 }}>
            Se generează o arhivă ZIP cu toate datele persoanei (date personale, cont, rapoarte de muncă, concedii, pontaj, documente din dosar, jurnal de acces),
            în formatul ales în Parametrii > GDPR. Exportul intră în jurnalul de audit.
          </div>
          <label className="label label-req">Parola dumneavoastră (pentru confirmare)</label>
          <PasswordInput value={gdprExp.pw} onChange={e => setGdprExp({ ...gdprExp, pw: e.target.value })} testId="gdpr-export-pw" autoComplete="current-password" name="onomad-actor-password" />
          <div style={{ display: "flex", gap: 8, justifyContent: "flex-end", marginTop: 16 }}>
            <button className="btn btn-secondary" onClick={() => setGdprExp(null)}>Anulează</button>
            <button className="btn btn-primary" data-testid="gdpr-export-go" onClick={async () => {
              try { await downloadPost(`/gdpr/persons/${gdprExp.person.id}/export`, { actor_password: gdprExp.pw }, "export-gdpr.zip"); setGdprExp(null); flash("Exportul a fost generat."); }
              catch (e) { setMsg({ text: e.message || "Eroare", type: "error" }); }
            }}>Exportă</button>
          </div>
        </Modal>
      )}

      {editing && (
        <Modal title={editing.id ? (editing.view ? "Angajat" : "Editează angajat") : "Angajat nou"} onClose={() => setEditing(null)} width={760}>
          <FormTabs value={tab} onChange={setTab} tabs={[
            { id: "general", label: "Date generale", warn: !editing.first_name?.trim() || !editing.last_name?.trim() },
            { id: "reges", label: "Date ReGES", hidden: !editing.sensitive_visible },
            { id: "custom", label: "Câmpuri personalizate", hidden: customDefs.filter(d => d.active).length === 0 },
            { id: "contracts", label: `Rapoarte de muncă${editing.id ? ` (${(editing.employments || []).length})` : ""}`, hidden: !editing.id },
          ]} />
          <ViewOnly on={editing.view && tab !== "contracts"}>
            <div className="grid-2">
              <div style={tabStyle(tab, "general")}>
                <div><label className="label label-req">Nume</label>
                  <input className="input" value={editing.last_name} onChange={e => setEditing({ ...editing, last_name: e.target.value })} data-testid="p-last" /></div>
                <div><label className="label label-req">Prenume</label>
                  <input className="input" value={editing.first_name} onChange={e => setEditing({ ...editing, first_name: e.target.value })} data-testid="p-first" /></div>
                <div><label className="label">CNP</label>
                  <input className="input" value={editing.cnp || ""} onChange={e => setEditing({ ...editing, cnp: e.target.value })} data-testid="p-cnp" /></div>
                <div><label className="label">Data nașterii</label>
                  <input className="input" type="date" value={editing.birth_date || ""} onChange={e => setEditing({ ...editing, birth_date: e.target.value })} data-testid="p-birth" /></div>
                <div><label className="label">Telefon</label>
                  <input className="input" value={editing.phone || ""} onChange={e => setEditing({ ...editing, phone: e.target.value })} data-testid="p-phone" /></div>
              </div>
              <div style={tabStyle(tab, "reges")}>
                {completeness && (
                  <div style={{ gridColumn: "1/-1", fontSize: 12, color: completeness.missing.length ? "#92400E" : "#166534" }} data-testid="reges-completeness">
                    Completitudine ReGES (câmpurile cerute de registru): {completeness.filled} din {completeness.total}. Câmpurile nu sunt obligatorii în platformă.
                  </div>)}
                <RegesFields kind="person" values={reg} prefix="pr" onChange={patch => setEditing(e => ({ ...e, reges: { ...e.reges, ...patch } }))} />
              </div>
              <div style={tabStyle(tab, "custom")}>
                <CustomFields entity="person" defs={customDefs} values={editing.custom}
                  employments={(editing.employments || []).map(e => ({ id: e.id, label: e.marca }))}
                  onChange={(key, item) => setEditing(e => ({ ...e, custom: { ...e.custom, [key]: item } }))} />
              </div>
            </div>
            {tab === "general" && !editing.id && <div style={{ marginTop: 10, fontSize: 11.5, color: "#475569" }}>Angajatul se creează fără acces în platformă (rol Salariat). Accesul se acordă ulterior din listă; rapoartele de muncă se adaugă din fișa lui sau din „Rapoarte de Muncă”.</div>}
          </ViewOnly>

          {tab === "general" && editing.id && (
            <div style={{ marginTop: 20, borderTop: "1px solid #E2E8F0", paddingTop: 16 }} data-testid="person-account">
              <div style={{ fontSize: 13, fontWeight: 700, color: "var(--navy)", marginBottom: 6 }}>Cont de autentificare</div>
              {editing.has_account
                ? <div style={{ fontSize: 12.5 }}>Utilizator: <b>{editing.username}</b>{editing.email ? ` · ${editing.email}` : ""}. Rolurile și parola se gestionează din „Utilizatori și Roluri”.</div>
                : <div style={{ fontSize: 12.5, color: "#64748B" }}>Fără cont: persoana nu se poate autentifica. Folosește „Acordă acces” din listă.</div>}
            </div>
          )}

          {tab === "contracts" && editing.id && (
            <div data-testid="person-employments">
              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", gap: 10, marginBottom: 6 }}>
                <div style={{ fontSize: 11.5, color: "#64748B" }}>Contractele de muncă ale angajatului. Se modifică din „Rapoarte de Muncă”.</div>
                {!editing.view && <button type="button" className="btn btn-primary" style={btn} data-testid="person-add-employment"
                  {...lock(has("employments.create"))} onClick={() => navigate(`/mdm/employments?person=${editing.id}`)}>+ Raport de muncă nou</button>}
              </div>
              {editing.employments == null
                ? <div style={{ fontSize: 12, color: "#94A3B8" }}>Nu ai dreptul să vezi rapoartele de muncă.</div>
                : editing.employments.length === 0
                  ? <div style={{ fontSize: 12, color: "#94A3B8" }}>Nu are niciun raport de muncă.</div>
                  : <div style={{ overflowX: "auto" }}><table className="tbl">
                      <thead><tr><th>Marcă</th><th>Structură</th><th>Funcție</th><th>Contract</th><th>Perioadă</th><th>Status</th></tr></thead>
                      <tbody>{editing.employments.map(e => {
                        const st = STATUS[e.status] || [e.status, ""];
                        return (
                          <tr key={e.id} data-testid={`person-empl-${e.marca}`}>
                            <td style={{ fontWeight: 600 }} className="tabular-nums">{e.marca}</td>
                            <td>{unitName(e.org_unit_id)}</td>
                            <td>{e.position_title || "—"}</td>
                            <td>{[e.contract_type_label, e.contract_number && `nr. ${e.contract_number}`].filter(Boolean).join(" ") || "—"}</td>
                            <td className="tabular-nums" style={{ whiteSpace: "nowrap" }}>{e.start_date || "—"} → {e.end_date || "prezent"}</td>
                            <td><span className={"badge " + st[1]}>{st[0]}</span></td>
                          </tr>);
                      })}</tbody>
                    </table></div>}
            </div>
          )}

          <div style={{ display: "flex", gap: 8, justifyContent: "flex-end", marginTop: 16 }}>
            <button className="btn btn-secondary" onClick={() => setEditing(null)}>{editing.view ? "Închide" : "Anulează"}</button>
            {!editing.view && <button className="btn btn-primary" onClick={save} data-testid="person-save">Salvează</button>}
          </div>
        </Modal>
      )}

      {grant && (
        <Modal title="Acordă acces în platformă" onClose={() => setGrant(null)} width={480}>
          <div style={{ fontSize: 13, marginBottom: 10 }}>{grant.person.last_name} {grant.person.first_name}</div>
          <label className="label">E-mail (opțional)</label>
          <input className="input" value={grant.email} onChange={e => setGrant({ ...grant, email: e.target.value })} data-testid="pgrant-email" />
          <label className="label" style={{ marginTop: 8 }}>Parolă (gol = se generează una temporară)</label>
          <input className="input" value={grant.password} onChange={e => setGrant({ ...grant, password: e.target.value })} data-testid="pgrant-password" />
          <div style={{ display: "flex", gap: 8, justifyContent: "flex-end", marginTop: 14 }}>
            <button className="btn btn-secondary" onClick={() => setGrant(null)}>Anulează</button>
            <button className="btn btn-primary" onClick={doGrant} data-testid="pgrant-confirm">Acordă acces</button>
          </div>
        </Modal>
      )}
      {grantDone && (
        <Modal title="Acces acordat" onClose={() => setGrantDone(null)} width={460}>
          <div style={{ fontSize: 13 }}>Utilizator: <b data-testid="pgrant-user">{grantDone.username}</b></div>
          <div style={{ fontSize: 13, marginTop: 4 }}>Parolă temporară: <b data-testid="pgrant-pass">{grantDone.password}</b></div>
          <div style={{ fontSize: 11.5, color: "#64748B", marginTop: 8 }}>Se afișează o singură dată. Persoana trebuie s-o schimbe la prima autentificare.{grantDone.emailed ? " Datele au fost trimise și pe e-mail." : ""}</div>
          <div style={{ display: "flex", justifyContent: "flex-end", marginTop: 14 }}><button className="btn btn-primary" onClick={() => setGrantDone(null)}>Am notat</button></div>
        </Modal>
      )}
      {revoke && (
        <Modal title="Revocă accesul" onClose={() => setRevoke(null)} width={460}>
          <div style={{ fontSize: 13 }}>Retragi accesul în platformă pentru <b>{revoke.last_name} {revoke.first_name}</b>? Angajatul rămâne înregistrat, cu rapoartele de muncă și datele lui; se poate acorda accesul din nou.</div>
          <div style={{ display: "flex", gap: 8, justifyContent: "flex-end", marginTop: 14 }}>
            <button className="btn btn-secondary" onClick={() => setRevoke(null)}>Anulează</button>
            <button className="btn btn-danger" onClick={doRevoke} data-testid="prevoke-confirm">Revocă accesul</button>
          </div>
        </Modal>
      )}
    </>
  );
}
